import {
  Caption1,
  DrawerBody,
  DrawerHeader,
  Input,
  OverlayDrawer,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { Add16Regular, Dismiss24Regular, Link16Regular } from "@fluentui/react-icons";
import { useMemo, useState } from "react";
import { FuiButton } from "@/components/fui-control";
import { SectionTitle } from "@/components/ui-typography";
import type { DocumentLinkTargetKind, PlanDocument } from "@/engine/types";
import { AttachmentPanel } from "@/features/tasks/AttachmentPanel";
import { DetailSection, DetailTabs, type DetailTab } from "@/features/tasks/detail-parts";
import { CommentThread, TaskActivityLog } from "@/features/tasks/TaskActivityFeed";
import { DateInput } from "@/features/tasks/pickers";
import { useCan } from "@/features/access/use-plan-access";
import { formatDate } from "@/lib/date-format";
import { recordCode } from "@/store/record-ops";
import { useProjectStore } from "@/store/project-store";
import { DocumentLinkList } from "./DocumentLinkList";
import { DocKindIcon } from "./DocLinkChip";
import { openDocumentTarget } from "./doc-open";
import { DOCUMENT_KIND_LABEL, DOCUMENT_VISIBILITY_LABEL, documentCode } from "./doc-model";
import type { DocReader } from "./doc-visibility";

const useStyles = makeStyles({
  drawer: {
    width: "440px",
    maxWidth: "100vw",
  },
  backdrop: {
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
    backgroundColor: "rgba(0, 0, 0, 0.12)",
  },
  header: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "32px",
    width: "100%",
  },
  headerMeta: {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    minWidth: 0,
  },
  code: {
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground2,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalXXL,
  },
  bodyFeed: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    overflowY: "hidden",
    height: "100%",
    minHeight: 0,
    gap: 0,
  },
  metaGrid: {
    display: "grid",
    gridTemplateColumns: "88px 1fr",
    rowGap: "8px",
    columnGap: tokens.spacingHorizontalS,
    alignItems: "center",
    fontSize: tokens.fontSizeBase300,
  },
  metaLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  metaValue: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  links: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  finder: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  hitList: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    maxHeight: "220px",
    overflowY: "auto",
  },
  hit: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    width: "100%",
    minWidth: 0,
    padding: "5px 8px",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground1,
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "1px" },
  },
  hitTitle: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexGrow: 1 },
  hitKind: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  muted: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorPaletteRedForeground1 },
});

const TARGET_KIND_LABEL: Record<DocumentLinkTargetKind, string> = {
  executionTask: "작업",
  scheduleTask: "일정",
  record: "이슈",
};

export function DocLinkFinder({
  docId,
  onDone,
}: {
  docId: string;
  onDone?: () => void;
}) {
  const styles = useStyles();
  const project = useProjectStore((s) => s.project);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const [query, setQuery] = useState("");
  const links = project.documentLinks.filter((link) => link.documentId === docId);

  const hits = useMemo(() => {
    const q = query.trim().toLowerCase();
    const taken = new Set(links.map((link) => `${link.targetKind}:${link.targetId}`));
    const out: Array<{ kind: DocumentLinkTargetKind; id: string; code?: string; title: string }> = [];
    const push = (kind: DocumentLinkTargetKind, id: string, title: string, code?: string) => {
      if (taken.has(`${kind}:${id}`)) return;
      if (q && !`${code ?? ""} ${title}`.toLowerCase().includes(q)) return;
      out.push({ kind, id, title, ...(code ? { code } : {}) });
    };
    for (const task of project.executionTasks) push("executionTask", task.id, task.title || "제목 없음");
    for (const task of project.tasks) push("scheduleTask", task.id, task.name || "이름 없는 작업", schedule.tasks.get(task.id)?.wbs);
    for (const record of project.records) push("record", record.id, record.title || "제목 없음", recordCode(record));
    return out.slice(0, 12);
  }, [query, links, project.executionTasks, project.tasks, project.records, schedule.tasks]);

  const selectHit = (hit: { kind: DocumentLinkTargetKind; id: string }) => {
    useProjectStore.getState().linkDocument(docId, hit.kind, hit.id);
    setQuery("");
    onDone?.();
  };

  return (
    <div className={styles.finder}>
      <Input
        size="small"
        placeholder="작업·일정·이슈 번호 또는 제목 검색"
        value={query}
        autoFocus
        aria-label="연결할 작업이나 이슈"
        onChange={(_, data) => setQuery(data.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            onDone?.();
          }
          if (event.key === "Enter" && hits[0]) {
            event.preventDefault();
            selectHit(hits[0]);
          }
        }}
      />
      <div className={styles.hitList}>
        {hits.length === 0 ? (
          <Caption1 className={styles.muted}>연결할 수 있는 항목이 없습니다</Caption1>
        ) : (
          hits.map((hit) => (
            <button
              key={`${hit.kind}:${hit.id}`}
              type="button"
              className={styles.hit}
              onClick={() => selectHit(hit)}
            >
              <span className={styles.hitKind}>{TARGET_KIND_LABEL[hit.kind]}</span>
              {hit.code ? <span className={styles.code}>{hit.code}</span> : null}
              <span className={styles.hitTitle}>{hit.title}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

export function DocRail({
  doc,
  reader,
  canEdit,
  uploadError,
  open = false,
  tab = "details",
  onTabChange,
  onClose,
}: {
  doc: PlanDocument;
  reader: DocReader;
  canEdit: boolean;
  uploadError?: string;
  open?: boolean;
  tab?: DetailTab;
  onTabChange?: (tab: DetailTab) => void;
  onClose?: () => void;
}) {
  const styles = useStyles();
  const project = useProjectStore((s) => s.project);
  const schedule = useProjectStore((s) => s.derived.schedule);
  const canComment = useCan("plan.comment.write") && !doc.deletedAt && (doc.visibility !== "private" || doc.authorId === reader.actorId);
  const [finding, setFinding] = useState(false);
  const links = project.documentLinks.filter((link) => link.documentId === doc.id);
  const activeComments = useMemo(() => doc.comments.filter((c) => !c.deleted).length, [doc.comments]);

  const catalog = useMemo(
    () => ({
      executionTasks: project.executionTasks,
      tasks: project.tasks.map((task) => ({ id: task.id, name: task.name, wbs: schedule.tasks.get(task.id)?.wbs, percentComplete: task.percentComplete })),
      records: project.records,
    }),
    [project.executionTasks, project.tasks, project.records, schedule.tasks],
  );

  const locked = !canEdit || !!doc.deletedAt;

  return (
    <OverlayDrawer
      position="end"
      open={open}
      modalType="non-modal"
      onOpenChange={(_, data) => {
        if (!data.open) onClose?.();
      }}
      className={styles.drawer}
      backdrop={{ className: styles.backdrop }}
      aria-label="문서 패널"
    >
      <DrawerHeader className={styles.header}>
        <div className={styles.headerTop}>
          <span className={styles.headerMeta}>
            <DocKindIcon kind={doc.kind} />
            <span className={styles.code}>{documentCode(doc.seq)}</span>
            <span>·</span>
            <span>{DOCUMENT_KIND_LABEL[doc.kind]}</span>
          </span>
          <FuiButton appearance="subtle" icon={<Dismiss24Regular />} aria-label="패널 닫기" onClick={onClose} />
        </div>
        <DetailTabs
          value={tab}
          onChange={(next) => onTabChange?.(next)}
          counts={{
            comments: activeComments,
            attachments: doc.attachments.length,
            activity: doc.activity.length,
          }}
          ariaLabel="문서 상세 탭"
        />
      </DrawerHeader>

      <DrawerBody className={tab === "comments" ? styles.bodyFeed : styles.body}>
        {tab === "details" ? (
          <>
            <DetailSection
              icon={<Link16Regular />}
              title="연결된 항목"
              hint={links.length ? `${links.length}` : undefined}
              action={
                locked ? null : (
                  <FuiButton appearance="subtle" size="small" icon={<Add16Regular />} onClick={() => setFinding((v) => !v)}>
                    연결
                  </FuiButton>
                )
              }
            >
              {finding && !locked ? <DocLinkFinder docId={doc.id} onDone={() => setFinding(false)} /> : null}
              {links.length === 0 ? (
                <Caption1 className={styles.muted}>작업이나 이슈를 연결하면 양쪽에서 이 글이 보입니다</Caption1>
              ) : (
                <DocumentLinkList
                  links={links}
                  catalog={catalog}
                  onOpen={(chip) => openDocumentTarget(chip.targetKind, chip.targetId)}
                  onUnlink={locked ? undefined : (linkId) => useProjectStore.getState().unlinkDocument(doc.id, linkId)}
                />
              )}
            </DetailSection>

            <DetailSection title="문서 속성">
              <div className={styles.metaGrid}>
                <span className={styles.metaLabel}>작성자</span>
                <span className={styles.metaValue}>{doc.authorId || "—"}</span>
                <span className={styles.metaLabel}>구분</span>
                <span className={styles.metaValue}>{DOCUMENT_VISIBILITY_LABEL[doc.visibility]}</span>
                {doc.kind === "meeting" ? (
                  <>
                    <span className={styles.metaLabel}>회의 날짜</span>
                    <div>
                      <DateInput
                        value={doc.heldAt?.slice(0, 10)}
                        disabled={locked}
                        ariaLabel="회의 날짜"
                        onChange={(value) => useProjectStore.getState().patchDocument(doc.id, { heldAt: value })}
                      />
                    </div>
                  </>
                ) : null}
                <span className={styles.metaLabel}>만듦</span>
                <span className={styles.metaValue}>{formatDate(doc.createdAt, "dateTime")}</span>
                <span className={styles.metaLabel}>수정</span>
                <span className={styles.metaValue}>{formatDate(doc.updatedAt, "dateTime")}</span>
              </div>
            </DetailSection>
          </>
        ) : null}

        {tab === "comments" ? (
          <CommentThread
            comments={doc.comments}
            readOnly={!canComment}
            onAdd={(body, options) => useProjectStore.getState().addDocumentComment(doc.id, body, options)}
            onUpdate={(commentId, body, options) => useProjectStore.getState().updateDocumentComment(doc.id, commentId, body, options)}
            onDelete={(commentId) => useProjectStore.getState().deleteDocumentComment(doc.id, commentId)}
          />
        ) : null}

        {tab === "attachments" ? (
          <div>
            {uploadError ? <Caption1 className={styles.error}>{uploadError}</Caption1> : null}
            <AttachmentPanel value={doc.attachments} readOnly={locked} onChange={(next) => useProjectStore.getState().setDocumentAttachments(doc.id, next)} />
          </div>
        ) : null}

        {tab === "activity" ? <TaskActivityLog items={doc.activity} /> : null}
      </DrawerBody>
    </OverlayDrawer>
  );
}
